import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  Button, CategoryChip, ChangeText, DataTable, FilterBar, FilterCheckbox, FilterSearch, FilterSelect, ImpactBadge, LiveNumber, StatTile, Tabs, useToast,
} from '../ui'
import type { Column } from '../ui'
import { BellIcon, FileIcon } from '../ui/icons'
import { api, ApiError, post } from '../lib/api'
import { useConfig } from '../lib/config'
import { useSession } from '../lib/session'
import { useStream } from '../lib/useStream'
import { useLiveStocks } from '../lib/liveMarket'
import { formatNumber, formatTimeIST } from '../lib/format'
import type { Filing, FilingStats, Page as FilingPage, TabKey } from '../lib/types'
import { TAB_CATEGORIES } from '../lib/types'
import { Page } from './Page'
import { RequireAuth } from '../app/RequireAuth'
import styles from './AnnouncementsPage.module.css'

const TABS: Array<{ id: TabKey; label: string }> = [
  { id: 'all', label: 'All' }, { id: 'results', label: 'Results' }, { id: 'actions', label: 'Corporate actions' },
  { id: 'board', label: 'Board meetings' }, { id: 'insider', label: 'Insider & SAST' }, { id: 'ratings', label: 'Credit ratings' },
  { id: 'orders', label: 'Orders & contracts' },
]

const DEFAULTS = { tab: 'all', min: '50', date: 'today' } as const

interface Filters { tab: TabKey; q: string; exchange: string; sector: string; mcap: string; min: string; date: string; mine: boolean }

function readFilters(p: URLSearchParams): Filters {
  const tab = (p.get('tab') ?? p.get('cat') ?? DEFAULTS.tab) as TabKey
  return {
    tab: tab in TAB_CATEGORIES ? tab : 'all',
    q: p.get('q') ?? '', exchange: p.get('exchange') ?? '', sector: p.get('sector') ?? '', mcap: p.get('mcap') ?? '',
    min: p.get('min') ?? DEFAULTS.min, date: p.get('date') ?? DEFAULTS.date, mine: p.get('mine') === '1',
  }
}

/** Filters → /api/v2 query string (tab/count views pass omitTab). */
function toQuery(f: Filters, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams()
  if (f.tab !== 'all') q.set('tab', f.tab)
  if (f.q.trim()) q.set('q', f.q.trim())
  if (f.exchange) q.set('exchange', f.exchange)
  if (f.sector) q.set('sector', f.sector)
  if (f.mcap) q.set('mcap', f.mcap)
  if (f.min !== '0') q.set('min_impact', f.min)
  if (f.date) q.set('date', f.date)
  if (f.mine) q.set('mine', '1')
  for (const [k, v] of Object.entries(extra)) q.set(k, v)
  return q.toString()
}

const istDate = (iso: string) => new Date(new Date(iso).getTime() + 5.5 * 3600e3).toISOString().slice(0, 10)
const todayIST = () => istDate(new Date().toISOString())

/** Does a streamed filing belong in the current view? (server re-checks delay/dup via GET /filings/:id) */
function matches(r: Filing, f: Filters): boolean {
  const cats = TAB_CATEGORIES[f.tab]
  if (cats && !cats.includes(r.category ?? '')) return false
  if (f.exchange && r.exchange !== f.exchange) return false
  if (f.sector && r.sector !== f.sector) return false
  if (f.mcap && r.mcap_bucket !== f.mcap) return false
  if (Number(f.min) > 0 && (r.impact_score ?? -1) < Number(f.min)) return false
  if (f.date === 'today' && istDate(r.filed_at) !== todayIST()) return false
  if (f.q.trim()) {
    const hay = `${r.symbol} ${r.company_name ?? ''} ${r.headline ?? ''} ${r.ai_summary_en ?? ''}`.toLowerCase()
    if (!f.q.toLowerCase().split(/\s+/).filter(Boolean).every(w => hay.includes(w))) return false
  }
  if (f.mine) return false // holdings view refreshes instead of live-inserting
  return true
}

function timeLabel(iso: string) {
  return istDate(iso) === todayIST()
    ? formatTimeIST(iso)
    : new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }).format(new Date(iso))
}

export function AnnouncementsPage() {
  return (
    <RequireAuth what="corporate announcements">
      <AnnouncementsDesk />
    </RequireAuth>
  )
}

function AnnouncementsDesk() {
  const [params, setParams] = useSearchParams()
  const f = useMemo(() => readFilters(params), [params])
  const { me } = useSession()
  const cfg = useConfig()
  const toast = useToast()
  const prices = cfg.prices_enabled
  const canExport = cfg.features.export_csv && me?.entitlements.export_csv === true

  const [rows, setRows] = useState<Filing[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [next, setNext] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [counts, setCounts] = useState<Record<TabKey, number> | null>(null)
  const [stats, setStats] = useState<FilingStats | null>(null)
  const [delay, setDelay] = useState(0)
  const [sectors, setSectors] = useState<string[]>([])
  const [fresh, setFresh] = useState<Set<number>>(new Set())
  const [qInput, setQInput] = useState(f.q)

  const setParam = useCallback((key: string, value: string, fallback = '') => {
    const nextParams = new URLSearchParams(params)
    nextParams.delete('cat')
    if (value === fallback) nextParams.delete(key)
    else nextParams.set(key, value)
    setParams(nextParams, { replace: true })
  }, [params, setParams])

  // Debounce the search box into the URL.
  useEffect(() => { setQInput(f.q) }, [f.q])
  useEffect(() => {
    const t = setTimeout(() => { if (qInput !== f.q) setParam('q', qInput) }, 300)
    return () => clearTimeout(t)
  }, [qInput, f.q, setParam])

  const query = toQuery(f)
  const load = useCallback(async () => {
    setState('loading')
    try {
      const [page, c] = await Promise.all([
        api<FilingPage<Filing>>(`/filings?${toQuery(f, { limit: '50' })}`),
        api<{ counts: Record<TabKey, number> }>(`/filings/counts?${toQuery({ ...f, tab: 'all' })}`),
      ])
      setRows(page.data); setNext(page.next_cursor); setDelay(page.delay_minutes); setCounts(c.counts); setState('ready')
    } catch { setState('error') }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `query` captures every filter
  }, [query])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    api<FilingStats>('/filings/stats').then(setStats).catch(() => undefined)
    api<{ data: Array<{ sector: string }> }>('/companies/sectors').then(r => setSectors(r.data.map(s => s.sector))).catch(() => undefined)
  }, [])

  const loadMore = async () => {
    if (!next) return
    setLoadingMore(true)
    try {
      const page = await api<FilingPage<Filing>>(`/filings?${toQuery(f, { limit: '50', cursor: next })}`)
      setRows(r => [...r, ...page.data.filter(x => !r.some(y => y.id === x.id))]); setNext(page.next_cursor)
    } finally { setLoadingMore(false) }
  }

  // Live: new or re-classified filings slide in; duplicates are removed.
  const refreshCounts = useRef<ReturnType<typeof setTimeout> | null>(null)
  useStream(['filing.created', 'filing.updated', 'filing.removed'], e => {
    const id = (e.data as { id?: number } | null)?.id
    if (!id) return
    if (e.type === 'filing.removed') { setRows(r => r.filter(x => x.id !== id)); return }
    api<{ data: Filing }>(`/filings/${id}`).then(({ data }) => {
      if (!matches(data, f)) { setRows(r => r.filter(x => x.id !== id)); return }
      setRows(r => [data, ...r.filter(x => x.id !== id)].sort((a, b) => b.filed_at.localeCompare(a.filed_at) || b.id - a.id))
      setFresh(s => new Set(s).add(id))
      setTimeout(() => setFresh(s => { const n = new Set(s); n.delete(id); return n }), 2200)
      if (refreshCounts.current) clearTimeout(refreshCounts.current)
      refreshCounts.current = setTimeout(() => {
        api<{ counts: Record<TabKey, number> }>(`/filings/counts?${toQuery({ ...f, tab: 'all' })}`).then(c => setCounts(c.counts)).catch(() => undefined)
        api<FilingStats>('/filings/stats').then(setStats).catch(() => undefined)
      }, 1500)
    }).catch(() => undefined)
  })

  const createRule = async (body: Record<string, unknown>, okMsg: string) => {
    try {
      const r = await post<{ downgraded: string | null }>('/alert-rules', body)
      toast.show({ tone: 'success', message: r.downgraded ? `${okMsg} Free plan alerts arrive in the daily digest.` : okMsg })
    } catch (e) {
      toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not create the alert rule.' })
    }
  }

  const saveFilters = () => createRule({
    scope: 'filters', channels: ['telegram'], min_impact: Number(f.min) || 0,
    ...(f.tab !== 'all' ? { tab: f.tab } : {}), ...(f.exchange ? { exchange: f.exchange } : {}),
    ...(f.sector ? { sector: f.sector } : {}), ...(f.mcap ? { mcap: f.mcap } : {}), ...(f.q.trim() ? { q: f.q.trim() } : {}),
  }, 'Saved as a Telegram alert.')

  // Live prices: LTP and "since filing" tick with the market stream (base = last close before the filing).
  const live = useLiveStocks()
  const ltpOf = (r: Filing) => live(r.symbol)?.ltp ?? r.ltp ?? null
  const sinceOf = (r: Filing) => { const l = ltpOf(r); return l && r.base_price ? Math.round((l / r.base_price - 1) * 10000) / 100 : r.since_filing_pct ?? null }
  const columns: Column<Filing>[] = [
    { key: 'time', header: 'Time', width: 64, numeric: true, sortValue: r => r.filed_at, render: r => <span className={styles.time}>{timeLabel(r.filed_at)}</span> },
    {
      key: 'company', header: 'Company', width: 170, sortValue: r => r.symbol, render: r => (
        <span className={styles.company}>
          <Link to={`/stock/${encodeURIComponent(r.symbol)}`} className={styles.sym}>{r.symbol}</Link>
          <span className={styles.name}>{r.company_name ?? r.exchange}</span>
        </span>
      ),
    },
    { key: 'category', header: 'Category', width: 132, hideBelow: 768, sortValue: r => r.category, render: r => r.category ? <CategoryChip category={r.category} /> : <span className={styles.pending}>Classifying…</span> },
    {
      key: 'headline', header: 'Announcement · AI summary', stackWide: true, render: r => (
        <span className={styles.head}>
          <span className={styles.title}>{r.headline ?? r.exchange_category}</span>
          <span className={styles.summary}>{r.ai_summary_en ?? r.exchange_category ?? ''}</span>
        </span>
      ),
    },
    { key: 'impact', header: 'Impact', width: 70, align: 'center', sortValue: r => r.impact_score, render: r => <ImpactBadge score={r.impact_score} /> },
    ...(prices ? [
      { key: 'ltp', header: 'LTP ₹', width: 90, align: 'right', numeric: true, hideBelow: 1024, sortValue: (r: Filing) => ltpOf(r), render: (r: Filing) => <LiveNumber value={ltpOf(r)} /> },
      { key: 'since', header: 'Since filing', width: 100, align: 'right', hideBelow: 1024, sortValue: (r: Filing) => sinceOf(r), render: (r: Filing) => <ChangeText percent={sinceOf(r)} weight="bold" /> },
    ] as Column<Filing>[] : []),
    {
      key: 'actions', header: 'Actions', width: 84, align: 'right', render: r => (
        <span className={styles.actions}>
          {r.pdf_url || r.source_url ? (
            <a href={r.pdf_url ?? r.source_url ?? '#'} target="_blank" rel="noopener noreferrer" className={styles.iconBtn} aria-label={`Open filing document for ${r.symbol} (new tab)`}>
              <FileIcon size={15} />
            </a>
          ) : null}
          <button type="button" className={styles.iconBtn} aria-label={`Alert me on future ${r.symbol} filings`}
            onClick={() => createRule({ scope: 'symbol', symbol: r.symbol, channels: ['telegram'] }, `Alert rule created for ${r.symbol}.`)}>
            <BellIcon size={15} />
          </button>
        </span>
      ),
    },
  ]

  const navigate = useNavigate()
  const exportHref = `/api/v2/filings/export.csv?${toQuery(f)}`
  const onExport = () => { if (!canExport) toast.show({ tone: 'info', message: 'CSV export is included in the Pro and Trader plans.', action: { label: 'See plans', onClick: () => navigate('/pricing') } }) }

  return (
    <Page
      title="Corporate announcements"
      subtitle={<>NSE + BSE · every filing read, classified and scored · updated live{delay > 0 ? ` · Free plan: ${delay}-minute delay` : ''}</>}
      actions={<>
        {canExport
          ? <a className={styles.linkBtn} href={exportHref} download>Export CSV</a>
          : <Button onClick={onExport} aria-describedby="export-note">Export CSV</Button>}
        <span id="export-note" className="sr-only">{canExport ? '' : 'Available on Pro and Trader plans'}</span>
        <Button variant="primary" onClick={() => void saveFilters()}>Save filters as Telegram alert</Button>
      </>}
    >
      {delay > 0 && (
        <div className={styles.delayNote} role="note">
          <span>Free plan: new filings appear {delay} minutes after they are published. Pro shows them the moment they land and alerts you instantly.</span>
          <Link to="/pricing">Upgrade</Link>
        </div>
      )}
      <div className={styles.stats}>
        <StatTile label="Filings today" value={stats ? formatNumber(stats.filings_today, 0) : null} loading={!stats} />
        <StatTile label="High impact (70+)" value={stats ? formatNumber(stats.high_impact_today, 0) : null} tone="primary" loading={!stats} />
        <StatTile label="On your holdings" loading={!stats}
          value={stats ? (stats.holdings_count ? formatNumber(stats.on_holdings_today, 0) : '0') : null}
          caption={stats && !stats.holdings_count ? <Link to="/portfolio">Import holdings</Link> : undefined} />
        <StatTile label="Median filing → alert" value={stats?.median_filing_to_alert_s != null ? `${stats.median_filing_to_alert_s} s` : null} />
      </div>

      <Tabs label="Announcement types" value={f.tab} onChange={id => setParam('tab', id, 'all')} controls="filings-table"
        tabs={TABS.map(t => ({ id: t.id, label: t.label, count: counts ? formatNumber(counts[t.id] ?? 0, 0) : undefined }))} />

      <FilterBar>
        <FilterSearch label="Search announcements" placeholder="Company or words in the filing" value={qInput} onChange={setQInput} />
        <FilterSelect label="Exchange" value={f.exchange} onChange={v => setParam('exchange', v)}
          options={[{ value: '', label: 'NSE + BSE' }, { value: 'NSE', label: 'NSE' }, { value: 'BSE', label: 'BSE' }]} />
        <FilterSelect label="Sector" value={f.sector} onChange={v => setParam('sector', v)}
          options={[{ value: '', label: 'All sectors' }, ...sectors.map(s => ({ value: s, label: s }))]} />
        <FilterSelect label="Market cap" value={f.mcap} onChange={v => setParam('mcap', v)}
          options={[{ value: '', label: 'All market caps' }, { value: 'large', label: 'Large cap' }, { value: 'mid', label: 'Mid cap' }, { value: 'small', label: 'Small cap' }, { value: 'micro', label: 'Micro cap' }]} />
        <FilterSelect label="Minimum impact" value={f.min} onChange={v => setParam('min', v, DEFAULTS.min)}
          options={[{ value: '50', label: 'Impact 50+' }, { value: '70', label: 'Impact 70+' }, { value: '0', label: 'Any impact' }]} />
        <FilterSelect label="Date" value={f.date} onChange={v => setParam('date', v, DEFAULTS.date)}
          options={[{ value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' }, { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' }]} />
        <FilterCheckbox label="My holdings only" checked={f.mine} onChange={v => setParam('mine', v ? '1' : '')} />
      </FilterBar>

      <div id="filings-table" className={styles.tableBox}>
        <DataTable
          caption="Corporate announcements"
          columns={columns}
          stackOnMobile
          rows={rows}
          rowKey={r => r.id}
          state={state === 'loading' && rows.length === 0 ? 'loading' : state === 'error' ? 'error' : 'ready'}
          emptyTitle={f.mine && !stats?.holdings_count ? 'No holdings yet' : 'No filings match these filters'}
          emptyBody={f.mine && !stats?.holdings_count ? <Link to="/portfolio">Import your holdings</Link> : 'Try “Any impact” or a longer date range.'}
          highlight={fresh}
          footer={rows.length > 0 ? (
            <>
              <span>Showing {formatNumber(rows.length, 0)} of {counts ? formatNumber(counts[f.tab] ?? rows.length, 0) : '…'} filings{Number(f.min) > 0 ? ` with impact ${f.min}+` : ''}</span>
              {next ? <Button variant="ghost" size="sm" loading={loadingMore} onClick={() => void loadMore()}>Load more</Button> : null}
            </>
          ) : undefined}
        />
      </div>
      <p className={styles.disclaimer}>{cfg.disclaimer}</p>
    </Page>
  )
}
